/**
 * Notification Modal — custom element
 *
 * Controls open/close lifecycle for the shared timed-release/back-in-stock modal.
 * Uses native <dialog> where supported, graceful fallback otherwise.
 *
 * Wiring:
 *   - Any element with [data-open-notification-modal="<modal-id>"] opens that modal
 *   - Elements with [data-modal-close] inside the modal close it
 *   - ESC key closes (native <dialog> gives us this free)
 *   - Focus trapped inside dialog (native <dialog> gives us this free; fallback uses trapFocus)
 *   - Returns focus to trigger element on close
 *
 * Feature-specific form submission is NOT handled here — timed-release-reminder.js
 * and back-in-stock-notification.js listen for submit on the inner form and handle
 * their own POST + Klaviyo logic.
 */
(function () {
  'use strict';

  if (customElements.get('notification-modal')) return;

  var hasNativeDialog = typeof HTMLDialogElement === 'function';

  class NotificationModal extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('.notification-modal__dialog');
      this.firstInput = this.querySelector('input:not([type="hidden"]):not([readonly])');
      this.lastTrigger = null;
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (!this.dialog) return;
      this.dialog.addEventListener('click', this.handleDialogClick);
      this.dialog.addEventListener('cancel', (e) => {
        // Native cancel fires on ESC — intercept so we can run our own close cleanup
        e.preventDefault();
        this.close();
      });

      // Live-format any phone input inside the modal as the user types
      // (inserts a space after the country code so "+61 420 000 000"
      // emerges from raw digit typing).
      var phoneInput = this.querySelector('[data-field-phone]');
      if (phoneInput) setupPhoneFormatting(phoneInput);
    }

    disconnectedCallback() {
      if (this.dialog) this.dialog.removeEventListener('click', this.handleDialogClick);
      document.removeEventListener('keydown', this.handleKeydown);
    }

    // ---------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------

    open(trigger) {
      if (!this.dialog) return;
      this.lastTrigger = trigger || document.activeElement;

      if (hasNativeDialog && typeof this.dialog.showModal === 'function') {
        try {
          this.dialog.showModal();
        } catch (e) {
          // Already open or detached — fall through to fallback
          this.dialog.setAttribute('open', '');
        }
      } else {
        this.dialog.setAttribute('open', '');
        document.addEventListener('keydown', this.handleKeydown);
      }

      document.body.classList.add('overflow-hidden');
      this.dispatchEvent(new CustomEvent('notification-modal:open', { bubbles: true }));

      // Focus first input after transition settles
      var modal = this;
      requestAnimationFrame(function () {
        var el = modal.firstInput || modal.querySelector('[data-notification-submit]');
        if (el && typeof trapFocus === 'function') {
          trapFocus(modal.dialog, el);
        } else if (el) {
          el.focus();
        }
      });
    }

    close() {
      if (!this.dialog) return;

      if (hasNativeDialog && this.dialog.open) {
        try { this.dialog.close(); } catch (e) { this.dialog.removeAttribute('open'); }
      } else {
        this.dialog.removeAttribute('open');
      }

      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove('overflow-hidden');

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.lastTrigger);
      } else if (this.lastTrigger && typeof this.lastTrigger.focus === 'function') {
        this.lastTrigger.focus();
      }

      this.dispatchEvent(new CustomEvent('notification-modal:close', { bubbles: true }));
    }

    isOpen() {
      return this.dialog && this.dialog.hasAttribute('open');
    }

    // ---------------------------------------------------------------------
    // Event handlers
    // ---------------------------------------------------------------------

    handleDialogClick(event) {
      var closer = event.target.closest('[data-modal-close]');
      if (closer) {
        event.preventDefault();
        this.close();
      }
    }

    handleKeydown(event) {
      // Only used in non-native fallback path — native <dialog> emits 'cancel' on ESC
      if (event.key === 'Escape' || event.code === 'Escape') {
        event.preventDefault();
        this.close();
      }
    }
  }

  customElements.define('notification-modal', NotificationModal);

  // ---------------------------------------------------------------------
  // Phone live-formatter
  //
  // The modal shows a visible "+" prefix outside the input and asks the
  // customer to type "country code + number". We auto-insert spaces as
  // they type so the input reads like "61 420 000 000" rather than a
  // run-on digit string.
  //
  // Strategy:
  //   1. Strip all non-digits from the current value
  //   2. Detect country-code length (1, 2 or 3 digits) against a small
  //      prefix table covering the ITU E.164 allocations
  //   3. Re-render as "<CC> <rest in chunks of 3>"
  //   4. Move caret to end — simplification that reads correctly for the
  //      overwhelmingly common case of users typing left-to-right
  // ---------------------------------------------------------------------

  // Numeric prefixes reserved as 1-digit country codes (NANP + Russia/KZ)
  var ONE_DIGIT_CC = { 1: true, 7: true };

  // 3-digit country codes (everything in the 2xx–9xx ranges that isn't
  // a 2-digit CC). Source: ITU E.164 Country Code list.
  var THREE_DIGIT_CC = (function () {
    var list = [
      // 2xx — Africa
      210, 211, 212, 213, 216, 218, 220, 221, 222, 223, 224, 225, 226, 227,
      228, 229, 230, 231, 232, 233, 234, 235, 236, 237, 238, 239, 240, 241,
      242, 243, 244, 245, 246, 247, 248, 249, 250, 251, 252, 253, 254, 255,
      256, 257, 258, 260, 261, 262, 263, 264, 265, 266, 267, 268, 269, 290,
      291, 297, 298, 299,
      // 3xx — small European/other
      350, 351, 352, 353, 354, 355, 356, 357, 358, 359, 370, 371, 372, 373,
      374, 375, 376, 377, 378, 379, 380, 381, 382, 383, 385, 386, 387, 389,
      // 4xx — more Europe + specialty
      420, 421, 423,
      // 5xx — Americas + Falkland
      500, 501, 502, 503, 504, 505, 506, 507, 508, 509, 590, 591, 592, 593,
      594, 595, 596, 597, 598, 599,
      // 6xx — Oceania + SE Asia specialty
      670, 672, 673, 674, 675, 676, 677, 678, 679, 680, 681, 682, 683, 685,
      686, 687, 688, 689, 690, 691, 692,
      // 8xx — global services
      800, 808, 850, 852, 853, 855, 856, 870, 878, 880, 881, 882, 883, 886,
      888,
      // 9xx — various Asia
      960, 961, 962, 963, 964, 965, 966, 967, 968, 970, 971, 972, 973, 974,
      975, 976, 977, 979, 992, 993, 994, 995, 996, 998
    ];
    var map = {};
    list.forEach(function (code) { map[code] = true; });
    return map;
  })();

  function detectCountryCode(digits) {
    if (!digits) return '';
    if (ONE_DIGIT_CC[digits[0]]) return digits.slice(0, 1);
    if (digits.length >= 3 && THREE_DIGIT_CC[digits.slice(0, 3)]) {
      return digits.slice(0, 3);
    }
    // Default: 2 digits (covers AU 61, UK 44, DE 49, FR 33, JP 81, etc.)
    return digits.slice(0, 2);
  }

  function formatPhone(digits) {
    if (!digits) return '';
    var cc = detectCountryCode(digits);
    if (digits.length <= cc.length) return cc;
    var rest = digits.slice(cc.length);
    var grouped = rest.match(/.{1,3}/g).join(' ');
    return cc + ' ' + grouped;
  }

  // ITU-T E.164 caps international phone numbers at 15 digits including the
  // country code. Anything past that is guaranteed invalid.
  var MAX_PHONE_DIGITS = 15;

  function setupPhoneFormatting(input) {
    input.addEventListener('input', function () {
      var digits = input.value.replace(/\D/g, '').slice(0, MAX_PHONE_DIGITS);
      var formatted = formatPhone(digits);
      if (formatted === input.value) return;
      input.value = formatted;
      // Caret to end — correct for the common case of linear typing;
      // acceptable compromise for mid-string edits (rare on a phone
      // field that most users fill in one pass).
      try {
        input.setSelectionRange(formatted.length, formatted.length);
      } catch (e) { /* some input types reject setSelectionRange — ignore */ }
    });
  }

  // ---------------------------------------------------------------------
  // Global delegated trigger — any [data-open-notification-modal] opens
  // the referenced modal. Value is the modal element id.
  // ---------------------------------------------------------------------
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-open-notification-modal]');
    if (!trigger) return;
    var modalId = trigger.getAttribute('data-open-notification-modal');
    if (!modalId) return;
    var modal = document.getElementById(modalId);
    if (!modal || typeof modal.open !== 'function') return;
    event.preventDefault();
    modal.open(trigger);
  });
})();
